import type { SupabaseClient } from '@supabase/supabase-js';
import type { SectorYearValue } from './types';

export const HDV_METHOD_VERSION = 'hdv-emfac2025-fleet-v1';
export const HDV_BACKCAST_METHOD_VERSION = 'hdv-emfac2025-county-backcast-v1';
export const HDV_FLEET_TABLE = 'emfac2025_fleet';
export const HDV_BACKCAST_TABLE = 'emfac2025_county_filtered';

const INVENTORY_YEARS = Array.from({ length: 2023 - 2005 + 1 }, (_, i) => 2005 + i);
const YOY_SMOOTHING_THRESHOLD = 0.5;

function computeHdvGhgFromFleetRow(totalVmt: number | null, gco2PerMile: number | null): number {
  if (!totalVmt || !gco2PerMile) return 0;
  return (totalVmt * gco2PerMile) / 1_000_000;
}

/** Apply 50% YoY smoothing — same rule as live GHG chart. */
export function applyHdvYoYSmoothing(byYear: Map<number, number>): Map<number, number> {
  const sortedYears = Array.from(byYear.keys()).sort((a, b) => a - b);
  const smoothed = new Map<number, number>();
  sortedYears.forEach((year, index) => {
    const current = byYear.get(year) || 0;
    if (index === 0) {
      smoothed.set(year, current);
      return;
    }
    const prevYear = sortedYears[index - 1];
    const prev = smoothed.get(prevYear) || 0;
    const changePct = prev > 0 ? Math.abs(current - prev) / prev : 1;
    smoothed.set(year, changePct > YOY_SMOOTHING_THRESHOLD ? prev : current);
  });
  return smoothed;
}

export async function fetchHdvFleetGhgByYear(
  supabase: SupabaseClient,
  geofips: number
): Promise<Map<number, number>> {
  const { data } = await supabase
    .from(HDV_FLEET_TABLE)
    .select('year, total_vmt, gco2_per_mile')
    .eq('geofips', geofips)
    .eq('class', 'HDV')
    .gte('year', 2015)
    .lte('year', 2023)
    .order('year', { ascending: true });

  const raw = new Map<number, number>();
  for (const row of data || []) {
    const year = Number(row.year);
    const ghg = computeHdvGhgFromFleetRow(row.total_vmt, row.gco2_per_mile);
    raw.set(year, (raw.get(year) || 0) + ghg);
  }
  return applyHdvYoYSmoothing(raw);
}

export async function fetchHdvBackcastingRatios(
  supabase: SupabaseClient,
  countyGeofips: number | null,
  countyNameVariations: string[]
): Promise<Map<number, number>> {
  const ratios = new Map<number, number>();
  for (const locName of countyNameVariations) {
    const { data } = await supabase
      .from(HDV_BACKCAST_TABLE)
      .select('year, backcasting_ratio')
      .eq('location_name', locName)
      .eq('class', 'HDV')
      .in('year', [2005, 2006, 2007, 2008, 2009, 2010, 2011, 2012, 2013, 2014])
      .order('year', { ascending: true });
    if (data && data.length > 0) {
      for (const row of data) ratios.set(Number(row.year), Number(row.backcasting_ratio));
      return ratios;
    }
  }
  if (countyGeofips) {
    const { data } = await supabase
      .from(HDV_BACKCAST_TABLE)
      .select('year, backcasting_ratio')
      .eq('geofips', countyGeofips)
      .eq('class', 'HDV')
      .in('year', [2005, 2006, 2007, 2008, 2009, 2010, 2011, 2012, 2013, 2014])
      .order('year', { ascending: true });
    for (const row of data || []) ratios.set(Number(row.year), Number(row.backcasting_ratio));
  }
  return ratios;
}

export function buildHdvEmissionsByYear(
  fleetByYear: Map<number, number>,
  backcastRatios: Map<number, number>
): Map<number, number> {
  const hdv2015 = fleetByYear.get(2015) || 0;
  const out = new Map<number, number>();
  for (const year of INVENTORY_YEARS) {
    if (year >= 2015) {
      out.set(year, Math.round(fleetByYear.get(year) || 0));
    } else {
      const ratio = backcastRatios.get(year);
      const ghg = ratio && hdv2015 > 0 ? hdv2015 * ratio : hdv2015;
      out.set(year, Math.round(ghg));
    }
  }
  return out;
}

export function hdvChartSeries(byYear: Map<number, number>): { year: number; emissions: number }[] {
  return INVENTORY_YEARS.map((year) => ({ year, emissions: byYear.get(year) || 0 }));
}

export async function fetchHeavyDutySectorValues(
  supabase: SupabaseClient,
  geofips: number,
  countyGeofips: number | null,
  countyNameVariations: string[]
): Promise<{ byYear: Map<number, SectorYearValue>; chartSeries: { year: number; category: string; emissions: number }[] }> {
  const fleet = await fetchHdvFleetGhgByYear(supabase, geofips);
  const ratios = await fetchHdvBackcastingRatios(supabase, countyGeofips, countyNameVariations);
  const emissions = buildHdvEmissionsByYear(fleet, ratios);
  const byYear = new Map<number, SectorYearValue>();
  for (const year of INVENTORY_YEARS) {
    const mt = emissions.get(year) ?? 0;
    byYear.set(year, {
      year,
      sectorId: 'transportation_heavy_duty',
      displayCategory: 'Heavy Duty Vehicles',
      emissionsMt: mt,
      status: 'calculated',
      methodVersion: year >= 2015 ? HDV_METHOD_VERSION : HDV_BACKCAST_METHOD_VERSION,
      source: year >= 2015 ? HDV_FLEET_TABLE : HDV_BACKCAST_TABLE,
    });
  }
  return {
    byYear,
    chartSeries: hdvChartSeries(emissions).map((r) => ({ ...r, category: 'Heavy Duty Vehicles' })),
  };
}
